import { browserStorage } from './browserStorage.js';

// A code file of the app that could not be loaded: after a new deployment, a
// tab opened on the previous build asks for files that no longer exist.
export function isChunkLoadError(error) {
  return error?.name === 'ChunkLoadError' || /Loading (CSS )?chunk [^ ]+ failed/i.test(String(error?.message || ''));
}

// FE-01: React.lazy keeps a failed load and throws it again on every render,
// so trying again cannot help; only a reload loads the code afresh. The lazy
// loaders (LazyWorkspaces.js) mark each failure they pass on, chunk or not,
// and the error boundary then offers Reload only.
const CODE_LOAD_FAILURE = Symbol.for('risk-tool.code-load-failure');

export function markCodeLoadFailure(error) {
  const failure = error instanceof Error ? error : new Error(String(error ?? 'A code file could not be loaded.'));
  try {
    Object.defineProperty(failure, CODE_LOAD_FAILURE, { value:true });
    return failure;
  } catch {
    // A frozen error: wrap it, keeping it as the cause.
    const wrapped = new Error(failure.message, { cause:failure });
    Object.defineProperty(wrapped, CODE_LOAD_FAILURE, { value:true });
    return wrapped;
  }
}

export function isCodeLoadFailure(error) {
  return isChunkLoadError(error) || Boolean(error && typeof error === 'object' && error[CODE_LOAD_FAILURE]);
}

// After a new deployment, a tab opened on the previous build asks for code
// files that no longer exist. Reload once to pick up the new build instead of
// showing a broken page; a second failure within a minute is a real error.
// Every failure passed on is marked as a failed load (FE-01): React.lazy keeps
// it, so the error boundary offers Reload only. The lazy pages
// (LazyWorkspaces.js) and App itself (AppRoot.js, FE-04) load through it.
const CHUNK_RELOAD_KEY = 'risk-tool:chunk-reload-at';
// The session store is reached only when a load fails, never while this
// module loads (FE-07: a browser that blocks site data throws on reaching it).
// A reload that cannot be recorded is not made, so a blocked store never
// reloads the page again and again; the failure is passed on instead.
export function reloadOnceForNewBuild(load, { storage, location = globalThis.location, now = () => Date.now() } = {}) {
  return () => load().catch(error => {
    if (!isChunkLoadError(error) || !location?.reload) throw markCodeLoadFailure(error);
    const store = storage === undefined ? browserStorage('sessionStorage') : storage;
    let last = 0;
    try { last = Number(store?.getItem(CHUNK_RELOAD_KEY)) || 0; } catch { last = 0; }
    if (now() - last < 60000) throw markCodeLoadFailure(error);
    let recorded = false;
    try { if (store) { store.setItem(CHUNK_RELOAD_KEY, String(now())); recorded = true; } } catch { recorded = false; }
    if (!recorded) throw markCodeLoadFailure(error);
    location.reload();
    return new Promise(() => {});
  });
}
